import SettingsService from "./services/SettingsService";
import Logger from './Logger';

async function getWindowManagementPermissionState() {
    let result;
    // The new permission name.
    try {
        (result = await navigator.permissions.query({
            name: 'window-management',
        }));
    } catch (err) {
        if (err.name !== 'TypeError') {
            return `${err.name}: ${err.message}`;
        }
        // The old permission name.
        try {
            result = await navigator.permissions.query({
                name: 'window-placement',
            });
        } catch (err) {
            if (err.name === 'TypeError') {
                return 'Window management not supported.';
            }
            return `${err.name}: ${err.message}`;
        }
    }

    console.log(JSON.stringify({ state: result.state, name: result.name }));
    if (result.state === 'prompt') {
        alert('Go to site settings and enable window management');
        Logger.log(
            'MultipleOutputScreens',
            'Alerted window management site setting is not enabled'
        );
        return null;
    }
    return result.state;
};

const logWindowAndScreen = (staticScreen) => {
    const w = staticScreen.window;
    return ({
        ...staticScreen,
        window: {
            name: w.name,
            url: w.location.href,
            closed: w.closed,
            devicePixelRatio: w.devicePixelRatio,
            fullScreen: w.fullScreen,
            innerHeight: w.innerHeight,
            innerWidth: w.innerWidth,
            outerHeight: w.outerHeight,
            outerWidth: w.outerWidth,
            screen: w.screen,
            screenX: w.screenX,
            screenY: w.screenY
        }
    });
};
const mapStaticScreen = (screen) => ({
    isPrimary: screen.isPrimary,
    availLeft: screen.availLeft,
    availTop: screen.availTop,
    availWidth: screen.availWidth,
    availHeight: screen.availHeight,
    devicePixelRatio: screen.devicePixelRatio,
    isInternal: screen.isInternal,
    label: screen.label,
    left: screen.left,
    top: screen.top,
    height: screen.height,
    width: screen.width
});

const openNewStaticScreenWindow = (screen, i) => {
    const staticScreen = mapStaticScreen(screen);
    staticScreen.windowIndex = i++;

    if (staticScreen.isPrimary) {
        staticScreen.window = window;
    } else {
        staticScreen.window = window.open(
            `${window.location.origin}${window.location.pathname}?secondary=true&window_index=${i++}&muted=1`,
            '_blank',
            `left=${staticScreen.availLeft},top=${staticScreen.availTop},width=${staticScreen.availWidth},height=${staticScreen.availHeight}`,
        );
        Logger.log(
            `MultipleOutputScreens`,
            `Opened window for output ${staticScreen.windowIndex}`,
            logWindowAndScreen(staticScreen)
        );
    }
    return staticScreen;
};

const isScreenInWindows = (screen, windows) => windows.find(win => (
        win.isPrimary === screen.isPrimary &&
        win.label === screen.label &&
        win.top === screen.top &&
        win.left === screen.left &&
        win.availTop === screen.availTop &&
        win.availLeft === screen.availLeft &&
        win.height === screen.height &&
        win.width === screen.width
));

class MultipleOutputScreens {
    constructor () {
        this.windows = [];
        this.handleScreensChange = this.handleScreensChange.bind(this);
        this.handleCurrentScreenChange = this.handleCurrentScreenChange.bind(this);
        this.checkWindows = this.checkWindows.bind(this);
    }

    handleScreensChange (event) {
        const newScreens = event.currentTarget.screens ?? [];
        if (newScreens.length <= 1) {
            // dont close primary thread
            const newWindows = [];
            this.windows.forEach(win => {
                if (!win.isPrimary) {
                    win.window.close();
                    Logger.log(
                        `MultipleOutputScreens`,
                        `Closed window for output ${win.windowIndex}`,
                        logWindowAndScreen(win)
                    );
                } else {
                    newWindows.push(win);
                }
            });
            this.windows = newWindows;
        } else if (newScreens.length < this.windows.length) {
            // implement for +3 outputs
        } else if (newScreens.length === this.windows.length) {
            // not sure how but do nothing
        } else if (newScreens.length > this.windows.length) {
            // open new windows
            let i = this.windows.length;
            for (const screen of newScreens) {
                if (!isScreenInWindows(screen, this.windows)) {
                    const openedScreen = openNewStaticScreenWindow(screen, i);
                    this.windows.push(openedScreen);
                }
            }
        }
        Logger.log(
            `MultipleOutputScreens`,
            `There are ${this.windows.length} windows opened for ${newScreens.length} screen displays`
        );
    }

    handleCurrentScreenChange (event) {
        const staticScreen = mapStaticScreen(event.currentTarget.currentScreen);
        const url = new URL(window.location.href);
        staticScreen.windowIndex = url.searchParams.get('window_index') ?? '';
        staticScreen.window = window;
        Logger.log(
            `MultipleOutputScreens`,
            `Screen changed on window ${staticScreen.windowIndex}`,
            logWindowAndScreen(staticScreen)
        );
    }

    checkWindows () {
        if (!SettingsService.hasFeature('multi_output') || !('getScreenDetails' in window)) return;
        getWindowManagementPermissionState().then(state => {
            console.log(`State: ${state}`);
            if (state === null) return;
            
            return window.getScreenDetails().then((sd) => {
                sd.addEventListener('currentscreenchange', this.handleCurrentScreenChange);
                if (!window.secondary) {
                    sd.addEventListener('screenschange', this.handleScreensChange);
                    let i = 0;
                    for (const screen of sd.screens) {
                        const staticScreen = openNewStaticScreenWindow(screen, i);
                        this.windows.push(staticScreen);
                    }
                }
            });
        });
    }
}

export default MultipleOutputScreens;